import React, { Children } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HeartIcon, UsersIcon, BookIcon, StoreIcon, BuildingIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
// Mock data for totals
const donationTotals = {
  platform: 15000,
  authors: 18500,
  vendors: 8500,
  readers: 5500,
  total: 47500
};
// Mock data for recent donations
const recentDonations = [{
  amount: 1250,
  cause: "Children's Literacy Foundation",
  donor: 'LitLaunch Community',
  date: '2023-12-01',
  genre: 'Romance'
}, {
  amount: 750,
  cause: 'Book Access for All',
  donor: 'Author Alliance',
  date: '2023-11-30',
  genre: 'Fantasy'
}, {
  amount: 500,
  cause: 'Libraries First',
  donor: "Reader's Circle",
  date: '2023-11-29',
  genre: 'Dark Romance'
}, {
  amount: 980,
  cause: 'Rural Library Project',
  donor: 'Fantasy Authors Guild',
  date: '2023-11-28',
  genre: 'Urban Fantasy'
}, {
  amount: 1500,
  cause: 'Literacy for All',
  donor: 'Thriller Writers Association',
  date: '2023-11-27',
  genre: 'Thriller'
}, {
  amount: 650,
  cause: 'Books for Schools',
  donor: 'Mystery Book Club',
  date: '2023-11-26',
  genre: 'Mystery'
}];
// Helper to slugify names for links
const slugify = (text: string) => text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
const Donations = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const statCards = [{
    type: 'platform',
    icon: HeartIcon,
    amount: donationTotals.total,
    label: 'Total Donated Across All Categories',
    color: 'indigo',
    link: '/donations/category/platform' // Using platform as the "all" view for now or could create a specific "all" view
  }, {
    type: 'authors',
    icon: UsersIcon,
    amount: donationTotals.authors,
    label: 'From Authors',
    color: 'blue',
    link: '/donations/category/authors'
  }, {
    type: 'readers',
    icon: BookIcon,
    amount: donationTotals.readers,
    label: 'From Readers',
    color: 'emerald',
    link: '/donations/category/readers'
  }, {
    type: 'vendors',
    icon: StoreIcon,
    amount: donationTotals.vendors,
    label: 'From Vendors',
    color: 'amber',
    link: '/donations/category/vendors'
  }, {
    type: 'platform',
    icon: BuildingIcon,
    amount: donationTotals.platform,
    label: 'Platform Contribution',
    color: 'rose',
    link: '/donations/category/platform'
  }];
  const colorMap: Record<string, {
    bg: string;
    bgDark: string;
    text: string;
    ring: string;
  }> = {
    indigo: {
      bg: 'bg-indigo-50',
      bgDark: 'bg-indigo-900/20',
      text: 'text-indigo-600',
      ring: 'ring-indigo-500'
    },
    blue: {
      bg: 'bg-blue-50',
      bgDark: 'bg-blue-900/20',
      text: 'text-blue-600',
      ring: 'ring-blue-500'
    },
    emerald: {
      bg: 'bg-emerald-50',
      bgDark: 'bg-emerald-900/20',
      text: 'text-emerald-600',
      ring: 'ring-emerald-500'
    },
    amber: {
      bg: 'bg-amber-50',
      bgDark: 'bg-amber-900/20',
      text: 'text-amber-600',
      ring: 'ring-amber-500'
    },
    rose: {
      bg: 'bg-rose-50',
      bgDark: 'bg-rose-900/20',
      text: 'text-rose-600',
      ring: 'ring-rose-500'
    }
  };
  return <div className={`min-h-screen ${dk.page}`} data-id="element-8666" data-mp="29kwa5kgxrqjc">
      {/* Hero */}
      <div className={`${dk.isDark ? 'bg-gradient-to-r from-indigo-900 to-purple-900' : 'bg-gradient-to-r from-indigo-600 to-purple-600'} text-white`} data-id="element-8667" data-mp="24jgphtio7m1h">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-8668" data-mp="3fzx97z19cou5">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-5 bg-white/15`} data-id="element-8669" data-mp="3g218u4s4j1om">
            <HeartIcon className="h-8 w-8 text-white" data-id="element-8670" data-mp="k2qyuswn8l1c" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" data-id="element-8671" data-mp="p2n2us5cccgw">
            Community Donations
          </h1>
          <p className="text-base sm:text-lg max-w-2xl mx-auto text-indigo-100" data-id="element-8672" data-mp="u1gjuuju6llt">
            Together, we're supporting a diverse range of nonprofits — from
            literacy programs and educational initiatives to social justice,
            health advocacy, and community support organizations. Every donation
            makes a difference.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8" data-id="element-8673" data-mp="1csr4wv2vs45s">
        <div className={`${dk.card} border rounded-xl shadow-lg p-5`} data-id="element-8674" data-mp="1cu29gx45hfxy">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-8675" data-mp="1hsvmkofiz4xl">
            {statCards.map((card, idx) => {
            const c = colorMap[card.color];
            const Icon = card.icon;
            return <Link key={idx} to={card.link} className={`rounded-lg p-5 text-center transition-all cursor-pointer hover:opacity-90 hover:scale-[1.02] ${dk.isDark ? c.bgDark : c.bg}`} data-id="element-8676" data-mp="329d1179ywktx" data-mpi={idx}>
                  <Icon className={`h-7 w-7 mx-auto mb-2 ${c.text}`} data-id="element-8677" data-mp="2scqgykpgtxsy" />
                  <div className={`text-xl font-bold ${c.text}`} data-id="element-8678" data-mp="3pz7bji6tdxb0">
                    ${card.amount.toLocaleString()}
                  </div>
                  <div className={`text-xs mt-1 font-medium ${c.text} opacity-80`} data-id="element-8679" data-mp="3l0mic2pzcdw2">
                    {card.label}
                  </div>
                </Link>;
          })}
          </div>
        </div>
      </div>

      {/* Recent Donations */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-8680" data-mp="cm47xuckgm9a">
        <h2 className={`text-xl font-bold mb-5 ${dk.textPrimary}`} data-id="element-8681" data-mp="7sj7lvni3g6x">
          Recent Donations
        </h2>
        <div className={`${dk.card} border rounded-xl shadow-sm overflow-hidden`} data-id="element-8682" data-mp="221op3qzinwaf">
          <div className="overflow-x-auto" data-id="element-8683" data-mp="2628m8uk3azwk">
            <table className="min-w-full" data-id="element-8684" data-mp="1sbllkwurqzxu">
              <thead data-id="element-8685" data-mp="1sakawtho0fgn">
                <tr className={`border-b ${dk.divider} ${dk.surfaceMuted}`} data-id="element-8686" data-mp="1s996qskb55p0">
                  {['Donor', 'Amount', 'Cause', 'Genre', 'Date'].map(h => <th key={h} className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-8687" data-mp="1icwb3p57chrm">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody className={`divide-y ${dk.divider}`} data-id="element-8688" data-mp="1eclw5mzfmh0m">
                {recentDonations.map((donation, index) => <tr key={index} className={`text-sm transition-colors ${dk.dropdownItemHover}`} data-id="element-8689" data-mp="33ra9bqfuk2zp" data-mpi={index}>
                    <td className={`px-5 py-3.5 font-medium ${dk.textPrimary}`} data-id="element-8690" data-mp="3isqugsicqitk">
                      {donation.donor}
                    </td>
                    <td className={`px-5 py-3.5 font-semibold ${dk.textPrimary}`} data-id="element-8691" data-mp="3dwa6byb5k4gh">
                      ${donation.amount.toLocaleString()}
                    </td>
                    <td className={`px-5 py-3.5`} data-id="element-8692" data-mp="3mt102428f77w">
                      <Link to={`/donations/nonprofit/${slugify(donation.cause)}`} className={`hover:underline ${dk.textSecondary} ${rt.textHover}`} data-id="element-8693" data-mp="5dugpfea85f3">
                        {donation.cause}
                      </Link>
                    </td>
                    <td className={`px-5 py-3.5 ${dk.textMuted}`} data-id="element-8694" data-mp="af1pnhs5vanc">
                      {donation.genre}
                    </td>
                    <td className={`px-5 py-3.5 ${dk.textMuted}`} data-id="element-8695" data-mp="2g8rpe07c7w1e">
                      {new Date(donation.date).toLocaleDateString()}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
};
export default Donations;